import { FC, useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Layout, Dropdown, Space, Button, Drawer } from 'antd';
import { LogoutOutlined, DoubleRightOutlined, DoubleLeftOutlined, UserOutlined, MenuOutlined, GlobalOutlined, PlusOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import useWindowDimensions from '../../hooks/dimensions.hook';
import { useDocumentMetrics } from '../../hooks/metrics.hook';
import { useTranslation } from 'react-i18next';
import LanguageMenu from '../molecules/language-menu.component';
import GlobalSearch from '../molecules/global-search.component';
import { Global } from '../../constants';

import { logOut } from '../../redux/authentication/actionCreator';
import Menu from '../organisms/menu.component';
import FooterSection from '../organisms/footer.component';

type Props = {
  logOut: Function,
  user?: any,
}

const AdminLayout: FC<Props> = ({ logOut, user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { Header, Footer, Sider, Content } = Layout;
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const { width } = useWindowDimensions();
  const { t, i18n } = useTranslation();
  const { data: metrics } = useDocumentMetrics();
  const pendientes = metrics?.kpis?.esperandoVobo ?? 0;
  // El rol legal solo revisa documentos: no ve el CTA de creación.
  const isLegal = user?.role === 'legal';

  // El logo que la empresa sube en Mi cuenta manda sobre el de IT DRAFT.
  // Si falla la descarga —la URL guardada es antigua, el archivo ya no
  // está— se vuelve al nuestro en vez de dejar el hueco roto.
  const logoEmpresa = user?.branchID?.logo || '';
  const [logoRoto, setLogoRoto] = useState(false);
  const usaLogoPropio = Boolean(logoEmpresa) && !logoRoto;
  const logoSrc = usaLogoPropio ? logoEmpresa : `${process.env.PUBLIC_URL}/logo.png`;

  // El tratamiento de la cápsula está calibrado para el arte de IT DRAFT
  // —teal oscuro, se aclara sobre la placa—. Un logo ajeno con ese filtro
  // sale lavado y con halo verde, así que lleva placa clara y sin filtro.
  const claseMarca = `sidebar__brand-mark${usaLogoPropio ? ' sidebar__brand-mark--propio' : ''}`;

  useEffect(() => {
    setLogoRoto(false);
  }, [logoEmpresa]);

  useEffect(() => {
    const driftScript = document.createElement('script');
    driftScript.type = 'text/javascript';
    driftScript.async = true;
    driftScript.crossOrigin = 'anonymous';
    driftScript.innerHTML = `
      "use strict";

      !function() {
        var t = window.driftt = window.drift = window.driftt || [];
        if (!t.init) {
          if (t.invoked) return void (window.console && console.error && console.error("Drift snippet included twice."));
          t.invoked = !0, t.methods = [ "identify", "config", "track", "reset", "debug", "show", "ping", "page", "hide", "off", "on" ], 
          t.factory = function(e) {
            return function() {
              var n = Array.prototype.slice.call(arguments);
              return n.unshift(e), t.push(n), t;
            };
          }, t.methods.forEach(function(e) {
            t[e] = t.factory(e);
          }), t.load = function(t) {
            var e = 3e5, n = Math.ceil(new Date() / e) * e, o = document.createElement("script");
            o.type = "text/javascript", o.async = !0, o.crossorigin = "anonymous", o.src = "https://js.driftt.com/include/" + n + "/" + t + ".js";
            var i = document.getElementsByTagName("script")[0];
            i.parentNode.insertBefore(o, i);
          };
        }
      }();
      drift.SNIPPET_VERSION = '0.3.1';
      drift.load('tx4xdytw89uw');
    `;
    document.head.appendChild(driftScript);

    return () => {
      // Clean up the script when the component is unmounted
      document.head.removeChild(driftScript);
    };
  }, []);

  const items: MenuProps['items'] = [
    {
      type: 'group',
      label: (
        <><span>{user.name}</span><br /><span>{user.email}</span></>
      ),
      children: [
        {
          type: 'divider',
        },
        {
          key: 'closeSesion',
          label: (
            <a onClick={() => handleCloseSesion()}>
              <LogoutOutlined /> {t("common.logout")}
            </a>
          ),
        },
      ],
    },
  ];


  const handleCloseSesion = () => {
    logOut();
  };

  const onToggleCollapsed = () => {
    if (!isMobile) {
      setCollapsed(!collapsed);
    } else {
      setShowMobileMenu(!showMobileMenu);
    }
  };

  useEffect(() => {
    if (width !== 0 && width <= 1024) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  }, [width]);

  useEffect(() => {
    if (isMobile) {
      setShowMobileMenu(false);
    }
  }, [location.pathname]);

  // Precarga en idle de los chunks de las rutas más visitadas del menú: la
  // primera navegación a cada una deja de mostrar el Suspense de página
  // completa. Mismos especificadores que los React.lazy de App, así se
  // calienta exactamente el mismo chunk.
  useEffect(() => {
    const prefetch = () => {
      import('../../pages/admin/documents.page');
      import('../../pages/admin/templates.page');
      import('../../pages/admin/document-wizard.page');
      import('../../pages/admin/document-detail.page');
      import('../../pages/admin/subscription.page');
      import('../../pages/admin/ai-creator.page');
    };

    const idle = (window as any).requestIdleCallback
      ? (window as any).requestIdleCallback(prefetch)
      : window.setTimeout(prefetch, 1500);

    return () => {
      if ((window as any).cancelIdleCallback) {
        (window as any).cancelIdleCallback(idle);
      } else {
        window.clearTimeout(idle as number);
      }
    };
  }, []);

  return (
    <div className="admin-layout">
      <Space direction="vertical" style={{ width: '100%' }} size={[0, 48]}>
        {/* Como en el entregable: el sidebar corre la altura completa a la
            izquierda y el header vive sobre la columna de contenido, así el
            bosque del menú no queda cortado por la barra blanca. */}
        <Layout hasSider>
          {!isMobile && (
            <Sider
              className={`sidebar${collapsed ? ' sidebar--collapsed' : ''}`}
              collapsed={collapsed}
              width={268}
            >
              <div className="sidebar__enter">
                {/* Marca dentro del sidebar, como el entregable: isotipo,
                    wordmark y la flecha de plegado en la misma fila. */}
                <div className="sidebar__brand">
                  {/* Cápsula con el logo completo, como la del login: el
                      lockup trae su propio "IT DRAFT"; al lado quedan el
                      chip CLM y la tagline. */}
                  <span aria-hidden="true" className={claseMarca}>
                    <img
                      alt=""
                      src={logoSrc}
                      onError={() => setLogoRoto(true)}
                      draggable={false}
                    />
                  </span>
                  <span className="sidebar__brand-text">
                    <span className="sidebar__brand-name">
                      {t('common.app_name')}
                      <i className="sidebar__brand-chip">CLM</i>
                    </span>
                    <span className="sidebar__brand-tag">{t('common.app_tagline')}</span>
                  </span>
                  <button
                    type="button"
                    aria-label={collapsed ? t('navbar.expand') : t('navbar.collapse')}
                    className="sidebar__collapse"
                    onClick={onToggleCollapsed}
                  >
                    {collapsed ? <DoubleRightOutlined /> : <DoubleLeftOutlined />}
                  </button>
                </div>

                {!isLegal && (
                  <button
                    type="button"
                    className="sidebar__cta"
                    onClick={() => navigate(Global.APP_URLS.DOCUMENT_WIZARD)}
                  >
                    <PlusOutlined />
                    <span>{t('home.new_document')}</span>
                  </button>
                )}
                <Menu inlineCollapsed={collapsed} pendientes={pendientes} />
              </div>
            </Sider>
          )}
          <Layout>
            <Header className="header">
              <>
                <div className="header__menu">
                  {/* En móvil el sidebar vive en el Drawer: este botón es la
                      única puerta al menú. En escritorio sobra (la flecha ya
                      está dentro del sidebar) y no se pinta. */}
                  {isMobile && (
                    <Button type="text" className="header__collapse" onClick={onToggleCollapsed}>
                      <MenuOutlined />
                    </Button>
                  )}
                  <GlobalSearch />
                </div>

                <div className="header__user__nav">
                  <Dropdown overlay={<LanguageMenu />} placement="bottomRight" trigger={['hover']}>
                    <Button type="text" className="header__icon-btn" icon={<GlobalOutlined />}>
                      {(i18n.language || 'es').slice(0, 2).toUpperCase()}
                    </Button>
                  </Dropdown>
                  <Dropdown menu={{ items }}>
                    <button onClick={(e) => e.preventDefault()} className="header__user__avatar"><UserOutlined /></button>
                  </Dropdown>
                </div>
              </>
            </Header>
            <Content className="content">
              <Outlet />
            </Content>
            <Footer>
              <FooterSection />
            </Footer>
          </Layout>
        </Layout>
      </Space>
      {/* Menú móvil: mismo bosque y misma piel que el sidebar de escritorio
          (ver .sidebar-drawer en sidebar.scss): marca completa en el título
          —cápsula, wordmark con chip y tagline— y CTA de creación arriba del
          menú, como en el riel de escritorio. */}
      <Drawer
        className="sidebar-drawer"
        title={(
          <div className="sidebar__brand sidebar__brand--drawer">
            <span aria-hidden="true" className={`${claseMarca} sidebar__brand-mark--drawer`}>
              <img alt="" src={logoSrc} onError={() => setLogoRoto(true)} draggable={false} />
            </span>
            <span className="sidebar__brand-text">
              <span className="sidebar__brand-name">
                {t('common.app_name')}
                <i className="sidebar__brand-chip">CLM</i>
              </span>
              <span className="sidebar__brand-tag">{t('common.app_tagline')}</span>
            </span>
          </div>
        )}
        placement="left"
        onClose={() => setShowMobileMenu(false)}
        open={showMobileMenu}
      >
        {!isLegal && (
          <button
            type="button"
            className="sidebar__cta"
            onClick={() => {
              navigate(Global.APP_URLS.DOCUMENT_WIZARD);
              setShowMobileMenu(false);
            }}
          >
            <PlusOutlined />
            <span>{t('home.new_document')}</span>
          </button>
        )}
        <Menu inlineCollapsed={false} pendientes={pendientes} />
      </Drawer>
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  user: state.users?.data || null,
});

export default connect(mapStateToProps, { logOut })(AdminLayout);